import * as querystring from "querystring";

import { focusAreaToArray, IFocusArea, IFocusAreaArray } from "./focus-area";
import { getGatewayDomain } from "./get-gateway-domain";
import getResizerDomain, { ResizerEnv, urlRegexes } from "./get-resizer-domain";
import { isAndroidOrChrome } from "./is-android-or-chrome";

export class Resize {
  public supportsWebP = isAndroidOrChrome();

  /**
   * Determines if the specified url is resizable
   *
   * @param {string} imgUrl The url to try to resize
   * @returns {boolean} True when imgUrl is a resizable url, meaning it's in a resizeable bucket and isn't already a resized url
   * @memberof Resize
   */
  public isResizableUrl(imgUrl: string): boolean {
    if (!this.getEnvFromUrl(imgUrl)) {
      return false;
    }
    const url = this.removeFullPath(imgUrl).replace(/^\//, "");
    if (url.length === 0) {
      return false;
    }
    return true;
  }

  /**
   * Gets a formatted URL that will returned the URL to the resized image.
   * If no image yet exists for that size, it will be generated on demand.
   * Assumes that the given URL is valid, does not check if the source exists.
   *
   * @param {string} targetKey The key in the bucket to get as the source (ex: workouts/123/cover.jpg)
   * @param options Resizer options to specify dimensions and offset
   * @returns {string} A full public URL that can be used to access the sized image
   * @memberof Resize
   */
  public getUrl(targetKey: string, options: IResizeOptions): string {
    if (!options.width && !options.height) {
      throw new Error("Cannot have both width and height be auto values");
    }
    if (
      [options.width, options.height]
        .filter(Boolean)
        .some(size => size < 1 || size > 3000)
    ) {
      throw new Error("Requested size is too small/large");
    }
    if (
      options.xOffsetDirection &&
      ["left", "right"].indexOf(options.xOffsetDirection) < 0
    ) {
      throw new Error("Invalid offset specified");
    }

    const envFromUrl = this.getEnvFromUrl(targetKey);
    if (!envFromUrl) {
      throw new Error("Unable to get environment from image URL.");
    }
    const formattedTargetKey = this.removeLeadingSlash(
      this.removeFullPath(targetKey)
    );

    return `https://${getResizerDomain(
      envFromUrl
    )}/${formattedTargetKey}?${this.getOrderedQueryString(options)}`;
  }

  /**
   * Returns the environment of the image URL it receives.
   *
   * @param {string} imgUrl The URL of the image
   * @returns {ResizerEnv} The environment of the image (ex: dev, test, or production)
   * @memberof Resize
   */
  public getEnvFromUrl(imgUrl: string): ResizerEnv {
    const envs: ResizerEnv[] = ["dev", "test", "production"];
    for (const key of envs) {
      if (urlRegexes[key].some(regex => regex.test(imgUrl))) {
        return key;
      }
    }
  }

  /**
   * Generates a PUT URL to update the focus area for a given image.
   *
   * @param {string} imgUrl The URL of the image
   * @returns {string} The full URL to make a PUT request to update the focus area
   * @memberof FocusArea
   */
  public getUpdateFocusAreaUrl(imgUrl: string): string {
    const envFromUrl = this.getEnvFromUrl(imgUrl);

    if (!envFromUrl) {
      throw new Error("Unable to get environment from image URL.");
    }

    const domain = getGatewayDomain(envFromUrl);
    const targetKey = this.removeLeadingSlash(this.removeFullPath(imgUrl));

    return `https://${domain}/image-auto-resizer/focus-area/${targetKey}`;
  }

  private getOrderedQueryString(options: IResizeOptions) {
    // exclude params that are null or set to undefined
    const params: IResizeQueryStringParams = {};
    if (options.width) {
      params.width = Math.round(options.width).toString();
    }
    if (options.height) {
      params.height = Math.round(options.height).toString();
    }
    if (options.xOffsetDirection) {
      params.xOffsetDirection = options.xOffsetDirection;
    }
    if (options.focusArea) {
      params.focusArea = focusAreaToArray(options.focusArea).join(",");
    }
    if (options.includeFocusAreaHeader) {
      params.includeFocusAreaHeader = "true";
    }
    if (options.nocache) {
      params.nocache = "true";
    }
    if (this.supportsWebP && options.allowWebP !== false) {
      params.format = "webp";
    }
    // and sort alphabetically
    return querystring
      .stringify(params)
      .split("&")
      .sort()
      .join("&");
  }

  private removeFullPath(url: string): string {
    const allEnvUrlRegexes = Object.keys(urlRegexes).reduce<RegExp[]>(
      (all, key) => [...all, ...urlRegexes[key]],
      []
    );
    return allEnvUrlRegexes.reduce(
      (sUrl, regex) => sUrl.replace(regex, ""),
      url
    );
  }

  private removeLeadingSlash(targetKey: string): string {
    return targetKey.replace(/^\//, "");
  }
}

interface IResizeQueryStringParams extends querystring.ParsedUrlQueryInput {
  width?: string;
  height?: string;
  xOffsetDirection?: string;
  focusArea?: string;
  includeFocusAreaHeader?: "true";
  nocache?: "true";
  format?: "webp";
}

export interface IResizeOptions {
  /** A number between 1 and 3000. The width of the resized image. At least width or height must be set */
  width?: number;
  /** A number between 1 and 3000. The height of the resized image. At least width or height must be set */
  height?: number;
  /** Either `left` or `right`, will request a crop with the subject offset to specified side */
  xOffsetDirection?: "left" | "right";
  focusArea?: IFocusArea | IFocusAreaArray;
  includeFocusAreaHeader?: true;
  nocache?: true;
  /** If WebP images should not be used for Android and Chrome clients */
  allowWebP?: boolean;
}
